
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Settings, Plus, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from '@/lib/supabase';

interface AlertSetting {
  id: string;
  symbol?: string;
  asset_category?: string;
  high_alert_threshold: number;
  medium_alert_threshold: number;
  min_reliability_high: number;
  min_reliability_medium: number;
  created_at: string;
  updated_at: string;
}

const ASSET_CATEGORIES = [
  'FOREX', 'CRYPTO', 'STOCK', 'COMMODITY', 'INDEX'
];

export const MarketAlertSettingsManagement = () => {
  const [settings, setSettings] = useState<AlertSetting[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingSetting, setEditingSetting] = useState<AlertSetting | null>(null);
  const [formData, setFormData] = useState({
    symbol: '',
    asset_category: '',
    high_alert_threshold: 1.0,
    medium_alert_threshold: 2.0,
    min_reliability_high: 4,
    min_reliability_medium: 3
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('market_alert_settings')
        .select('*')
        .order('symbol', { ascending: true });

      if (error) throw error;
      setSettings(data || []);
    } catch (error) {
      console.error('Error loading alert settings:', error);
      toast.error('Failed to load alert settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const dataToSave = {
        ...formData,
        symbol: formData.symbol || null,
        asset_category: formData.asset_category || null
      };

      if (editingSetting) {
        const { error } = await supabase
          .from('market_alert_settings')
          .update(dataToSave)
          .eq('id', editingSetting.id);
        if (error) throw error;
        toast.success('Alert setting updated successfully');
      } else {
        const { error } = await supabase
          .from('market_alert_settings')
          .insert([dataToSave]);
        if (error) throw error;
        toast.success('Alert setting created successfully');
      }

      setIsDialogOpen(false);
      setEditingSetting(null);
      resetForm();
      loadSettings();
    } catch (error: any) {
      console.error('Error saving alert setting:', error);
      toast.error(error.message || 'Failed to save alert setting');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this alert setting?')) return;

    try {
      const { error } = await supabase
        .from('market_alert_settings')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success('Alert setting deleted successfully');
      loadSettings();
    } catch (error: any) {
      console.error('Error deleting alert setting:', error);
      toast.error(error.message || 'Failed to delete alert setting');
    }
  };

  const handleEdit = (setting: AlertSetting) => {
    setEditingSetting(setting);
    setFormData({
      symbol: setting.symbol || '',
      asset_category: setting.asset_category || '',
      high_alert_threshold: setting.high_alert_threshold,
      medium_alert_threshold: setting.medium_alert_threshold,
      min_reliability_high: setting.min_reliability_high,
      min_reliability_medium: setting.min_reliability_medium
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      symbol: '',
      asset_category: '',
      high_alert_threshold: 1.0,
      medium_alert_threshold: 2.0,
      min_reliability_high: 4,
      min_reliability_medium: 3
    });
  };

  const handleAddNew = () => {
    setEditingSetting(null);
    resetForm();
    setIsDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-6 w-6 text-orange-500" />
          <h1 className="text-2xl font-bold">Market Alert Settings</h1>
        </div>
        <Button onClick={handleAddNew}>
          <Plus className="h-4 w-4 mr-2" />
          Add Setting
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Alert Threshold Configuration</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Symbol/Category</TableHead>
                  <TableHead>High Alert (%)</TableHead>
                  <TableHead>Medium Alert (%)</TableHead>
                  <TableHead>Min Reliability</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8">
                      Loading settings...
                    </TableCell>
                  </TableRow>
                ) : settings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8">
                      No alert settings configured
                    </TableCell>
                  </TableRow>
                ) : (
                  settings.map((setting) => (
                    <TableRow key={setting.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {setting.symbol ? (
                            <Badge variant="default">{setting.symbol}</Badge>
                          ) : (
                            <Badge variant="secondary">{setting.asset_category}</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{setting.high_alert_threshold}%</TableCell>
                      <TableCell>{setting.medium_alert_threshold}%</TableCell>
                      <TableCell>
                        H:{setting.min_reliability_high} / M:{setting.min_reliability_medium}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(setting)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(setting.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingSetting ? 'Edit Alert Setting' : 'Add Alert Setting'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Symbol (Optional)</Label>
                <Input
                  placeholder="e.g., EURUSD"
                  value={formData.symbol}
                  onChange={(e) => setFormData({ ...formData, symbol: e.target.value.toUpperCase() })}
                />
              </div>
              <div className="space-y-2">
                <Label>Asset Category</Label>
                <Select
                  value={formData.asset_category}
                  onValueChange={(value) => setFormData({ ...formData, asset_category: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {ASSET_CATEGORIES.map((category) => (
                      <SelectItem key={category} value={category}>
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>High Alert Threshold (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.high_alert_threshold}
                  onChange={(e) => setFormData({ ...formData, high_alert_threshold: parseFloat(e.target.value) })}
                />
              </div>
              <div className="space-y-2">
                <Label>Medium Alert Threshold (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.medium_alert_threshold}
                  onChange={(e) => setFormData({ ...formData, medium_alert_threshold: parseFloat(e.target.value) })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Min Reliability (High)</Label>
                <Input
                  type="number"
                  min="1"
                  max="5"
                  value={formData.min_reliability_high}
                  onChange={(e) => setFormData({ ...formData, min_reliability_high: parseInt(e.target.value) })}
                />
              </div>
              <div className="space-y-2">
                <Label>Min Reliability (Medium)</Label>
                <Input
                  type="number"
                  min="1"
                  max="5"
                  value={formData.min_reliability_medium}
                  onChange={(e) => setFormData({ ...formData, min_reliability_medium: parseInt(e.target.value) })}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave}>
                {editingSetting ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
